import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  approveLeaveRequest,
  cancelLeaveRequest,
  createHoliday,
  createLeaveRequest,
  createLeaveType,
  deleteHoliday,
  deleteLeaveType,
  editHoliday,
  editLeaveType,
  getHolidays,
  getLeaveRequests,
  getLeaveTypes,
  getMyBalances,
  getMyLeaveRequests,
  rejectLeaveRequest,
  restoreLeaveType,
  updateLeaveBalance,
} from "api/leaveApi"
import {
  useCreateResource,
  useDeleteResource,
  useEditResource,
  usePaginatedResource,
} from "./useCrudResource"

export const LEAVE_QUERY_KEY = "leave"

export const useLeaveTypes = (params = {}, enabled = true) =>
  useQuery({
    queryKey: [LEAVE_QUERY_KEY, "types", JSON.stringify(params)],
    queryFn: () => getLeaveTypes(params),
    enabled,
  })

export const useCreateLeaveType = () =>
  useCreateResource(createLeaveType, LEAVE_QUERY_KEY)

export const useEditLeaveType = () =>
  useEditResource(editLeaveType, LEAVE_QUERY_KEY)

export const useDeleteLeaveType = () =>
  useDeleteResource(deleteLeaveType, LEAVE_QUERY_KEY)

export const useRestoreLeaveType = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: restoreLeaveType,
    onSuccess: () => qc.invalidateQueries({ queryKey: [LEAVE_QUERY_KEY] }),
  })
}

export const useHolidays = (params = {}, enabled = true) =>
  useQuery({
    queryKey: [LEAVE_QUERY_KEY, "holidays", JSON.stringify(params)],
    queryFn: () => getHolidays(params),
    enabled,
  })

export const useCreateHoliday = () =>
  useCreateResource(createHoliday, LEAVE_QUERY_KEY)

export const useEditHoliday = () =>
  useEditResource(editHoliday, LEAVE_QUERY_KEY)

export const useDeleteHoliday = () =>
  useDeleteResource(deleteHoliday, LEAVE_QUERY_KEY)

export const useLeaveRequests = (params = {}, enabled = true) =>
  usePaginatedResource(LEAVE_QUERY_KEY, getLeaveRequests, params, enabled)

export const useMyLeaveRequests = (params = {}) =>
  useQuery({
    queryKey: [LEAVE_QUERY_KEY, "my-requests", JSON.stringify(params)],
    queryFn: () => getMyLeaveRequests(params),
    keepPreviousData: true,
  })

export const useCreateLeaveRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createLeaveRequest,
    onSuccess: () => qc.invalidateQueries({ queryKey: [LEAVE_QUERY_KEY] }),
  })
}

export const useApproveLeaveRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: approveLeaveRequest,
    onSuccess: () => qc.invalidateQueries({ queryKey: [LEAVE_QUERY_KEY] }),
  })
}

export const useRejectLeaveRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: rejectLeaveRequest,
    onSuccess: () => qc.invalidateQueries({ queryKey: [LEAVE_QUERY_KEY] }),
  })
}

export const useCancelLeaveRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cancelLeaveRequest,
    onSuccess: () => qc.invalidateQueries({ queryKey: [LEAVE_QUERY_KEY] }),
  })
}

export const useMyBalances = (params = {}) =>
  useQuery({
    queryKey: [LEAVE_QUERY_KEY, "balances", JSON.stringify(params)],
    queryFn: () => getMyBalances(params),
  })

export const useUpdateLeaveBalance = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateLeaveBalance,
    onSuccess: () => qc.invalidateQueries({ queryKey: [LEAVE_QUERY_KEY] }),
  })
}